import { Clock, CalendarClock, Laptop } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Only these three Good to know categories ever surface on browse cards.
 * Every other category stays on the café detail page. This list is fixed, so
 * a card can never render more than three limitation pills.
 */
const LIMITATION_ORDER = ["time_limit", "wifi_schedule", "laptop_policy"] as const;

type LimitationCategory = (typeof LIMITATION_ORDER)[number];

const LIMITATION_META: Record<
  LimitationCategory,
  { icon: React.ComponentType<{ className?: string }>; label: string }
> = {
  time_limit: { icon: Clock, label: "Timed WiFi" },
  wifi_schedule: { icon: CalendarClock, label: "WiFi hours" },
  laptop_policy: { icon: Laptop, label: "Laptop hours" },
};


/** Filtered, ordered list of surfaceable limitation categories. */
export function getLimitationCategories(
  categories?: string[] | null
): LimitationCategory[] {
  if (!categories || categories.length === 0) return [];
  return LIMITATION_ORDER.filter((c) => categories.includes(c));
}

interface LimitationPillsProps {
  categories?: string[] | null;
  className?: string;
}

/**
 * Soft amber heads up pills. Renders nothing (no DOM node at all) when the
 * café has none of the surfaceable categories, so card height never changes.
 */
export function LimitationPills({ categories, className }: LimitationPillsProps) {
  const surfaced = getLimitationCategories(categories);
  if (surfaced.length === 0) return null;

  return (
    <>
      {surfaced.map((key) => {
        const { icon: Icon, label } = LIMITATION_META[key];
        return (
          <span
            key={key}
            aria-label={label}
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium border",
              className
            )}
            style={{
              backgroundColor: "#FAEEDA",
              borderColor: "#EBD3A0",
              color: "#8A5A14",
            }}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </span>
        );
      })}
    </>
  );
}
